import { Button, CheckBox, Loader, SearchInput } from 'components';
import { LIMIT_MEMBERS, NOT_FOUND } from 'constants/errors';
import _ from 'lodash';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { InfoIcon } from 'static';
import { doRequestCommunityMatrix } from 'store/actionCreators/communities';
import { getProfileName } from 'utils';

import { MatrixList } from './styled';
import { MATRIX_CELL_SIZE, useMatrixWindow } from './useMatrixWindow';

const defaultState = null;

const CommunitiesList = ({
  authUser,
  communities,
  deleteCommunityUser,
  addCommunityUsers,
  loadingMatrix,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const scrollRef = useRef(null);
  const leftRef = useRef(null);
  const [dataUsers, setDataUsers] = useState([]);
  const [dataCommunities, setDataCommunities] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [filterUsersData, setFilterUsersData] = useState(null);
  const [selectedData, setSelectedData] = useState(defaultState);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    dispatch(doRequestCommunityMatrix());
  }, [dispatch]);

  useEffect(() => {
    if (communities && communities.length) {
      const usersData = [];
      communities.forEach((item) => {
        if (item?.users && Array.isArray(item.users)) {
          usersData.push(...item.users);
        }
      });

      setDataUsers(_.uniqBy(usersData, 'id'));

      setDataCommunities(
        communities.map((item) => ({
          ...item,
          userIds: item?.users?.map((user) => user.id).filter(Boolean) || [],
        })),
      );
      setIsInitialized(true);
    } else if (communities && communities.length === 0) {
      setDataUsers([]);
      setDataCommunities([]);
      setIsInitialized(true);
    }
  }, [communities]);

  useEffect(() => {
    if (isInitialized && !loadingMatrix) {
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [isInitialized, loadingMatrix]);

  useEffect(() => {
    if (searchInputValue && dataUsers?.length) {
      const filterUser = dataUsers.filter((user) => {
        if (!user?.profile) return false;
        const fullName = `${user.profile.firstName || ''} ${
          user.profile.lastName || ''
        }`.trim();
        return fullName.toLowerCase().includes(searchInputValue.toLowerCase());
      });

      setFilterUsersData(filterUser);
    } else {
      setFilterUsersData(null);
    }
  }, [searchInputValue, dataUsers]);

  const successMessage = () =>
    addToast(t('community.leaveSuccess'), {
      appearance: 'success',
      autoDismiss: true,
    });

  const failureMessage = (e) => {
    setSelectedData(defaultState);
    if (e?.data?.error === LIMIT_MEMBERS) {
      addToast(t('communitySettings.disableAddMemberTooltip'), {
        appearance: 'error',
        autoDismiss: false,
      });
      return;
    }

    setDataCommunities(
      dataCommunities.map((item) =>
        item.id === selectedData?.communityId
          ? {
              ...item,
              userIds: item.userIds.filter(
                (ids) => ids !== selectedData?.userId,
              ),
            }
          : item,
      ),
    );

    if (e?.data?.error === NOT_FOUND) {
      addToast(t('subscriptions.subscriptionExpired'), {
        appearance: 'error',
        autoDismiss: false,
      });
    } else {
      addToast(t('errors.text500'), {
        appearance: 'error',
        autoDismiss: false,
      });
    }
  };

  const onChangeCheckBox = (e, id, community) => {
    if (e.target.checked) {
      setDataCommunities(
        dataCommunities.map((item) =>
          item.id === community.id
            ? { ...item, userIds: [...item.userIds, id] }
            : item,
        ),
      );
      setSelectedData({ communityId: community.id, userId: id });
      addCommunityUsers({
        communityId: community.id,
        onSuccess: successMessage,
        onFailure: failureMessage,
        userIds: [id],
        isCurrentUser: true,
      });
    } else {
      setDataCommunities(
        dataCommunities
          .map((item) =>
            item.id === community.id
              ? {
                  ...item,
                  userIds: item.userIds.filter((userId) => userId !== id),
                }
              : item,
          )
          .filter(
            (item) =>
              item.ownerId === authUser.id ||
              item.userIds.includes(authUser.id),
          ),
      );
      deleteCommunityUser({
        communityId: community.id,
        userIds: [id],
        isCurrentUser: id === authUser.id,
      });
    }
  };

  const users = useMemo(() => {
    if (filterUsersData) return filterUsersData;
    if (dataUsers) return dataUsers;
    return [];
  }, [dataUsers, filterUsersData]);

  const isCheckboxDisabled = (user, com) =>
    user && com && user.id === com.ownerId;

  const shouldShowLoading = loadingMatrix || !isInitialized || !showContent;
  const hasNoData =
    showContent &&
    isInitialized &&
    !loadingMatrix &&
    (!dataCommunities?.length || !users?.length);
  const hasNoSearchResults =
    showContent &&
    isInitialized &&
    !loadingMatrix &&
    searchInputValue &&
    !users?.length;

  const shouldShowEmptyState = hasNoData || hasNoSearchResults;
  const emptyStateType = hasNoSearchResults ? 'search' : 'noData';

  const showMatrix =
    !shouldShowLoading &&
    !shouldShowEmptyState &&
    users?.length > 0 &&
    dataCommunities?.length > 0;

  const { startRow, endRow, startCol, endCol } = useMatrixWindow({
    scrollRef,
    leftRef,
    rowCount: users.length,
    colCount: dataCommunities.length,
    enabled: showMatrix,
  });

  const gridWidth = dataCommunities.length * MATRIX_CELL_SIZE;
  const gridHeight = users.length * MATRIX_CELL_SIZE;
  const rowLeft = startCol * MATRIX_CELL_SIZE;

  const visibleUsers = useMemo(() => {
    const rows = [];
    for (let index = startRow; index < endRow; index += 1) {
      const user = users[index];
      if (user) {
        rows.push({ user, index });
      }
    }
    return rows;
  }, [users, startRow, endRow]);

  const visibleCommunities = useMemo(() => {
    const cols = [];
    for (let index = startCol; index < endCol; index += 1) {
      const item = dataCommunities[index];
      if (item) {
        cols.push(item);
      }
    }
    return cols;
  }, [dataCommunities, startCol, endCol]);

  if (!Array.isArray(communities)) {
    return (
      <MatrixList.EmptyState>
        <MatrixList.EmptyState.Icon>⚠️</MatrixList.EmptyState.Icon>
        <MatrixList.EmptyState.Title>
          {t('communitySettings.error.title', 'Error Loading Data')}
        </MatrixList.EmptyState.Title>
        <MatrixList.EmptyState.Description>
          {t(
            'communitySettings.error.description',
            'Unable to load community data. Please try refreshing the page.',
          )}
        </MatrixList.EmptyState.Description>
      </MatrixList.EmptyState>
    );
  }

  return (
    <>
      <MatrixList.Description>
        <InfoIcon
          width={14}
          height={14}
          style={{ marginRight: '6px', marginBottom: '-2px' }}
        />
        <Trans
          i18nKey="communitySettings.matrixDescription"
          components={{ brPure: <br /> }}
        />
      </MatrixList.Description>

      <MatrixList ref={scrollRef} isCenter={shouldShowEmptyState}>
        {shouldShowLoading && <Loader />}
        {!shouldShowLoading && shouldShowEmptyState && (
          <MatrixList.EmptyState>
            <MatrixList.EmptyState.Icon>
              {emptyStateType === 'search' ? '🔍' : '📊'}
            </MatrixList.EmptyState.Icon>
            <MatrixList.EmptyState.Title>
              {emptyStateType === 'search'
                ? t(
                    'communitySettings.noSearchResults.title',
                    'No Search Results',
                  )
                : t(
                    'communitySettings.noMatrixData.title',
                    'No Matrix Data Available',
                  )}
            </MatrixList.EmptyState.Title>
            <MatrixList.EmptyState.Description>
              {emptyStateType === 'search'
                ? t('communitySettings.noSearchResults.description', {
                    searchResult: `"${searchInputValue.trim()}"`,
                  }).replace(/&quot;/g, '"')
                : t(
                    'communitySettings.noMatrixData.description',
                    'There are no communities or users to display in the matrix. Create communities and add users to see the matrix view.',
                  )}
              {emptyStateType === 'search' && (
                <MatrixList.EmptyState.ButtonWrapper>
                  <Button
                    variant="default"
                    width="100%"
                    onClick={() => setSearchInputValue('')}
                  >
                    {t('communitySettings.clearSearch', 'Clear Search')}
                  </Button>
                </MatrixList.EmptyState.ButtonWrapper>
              )}
            </MatrixList.EmptyState.Description>
          </MatrixList.EmptyState>
        )}

        {showMatrix && (
          <>
            <MatrixList.Left ref={leftRef}>
              <MatrixList.SearchContainer>
                <SearchInput
                  width="100%"
                  onChange={(e) => setSearchInputValue(e.target.value)}
                  value={searchInputValue}
                  placeholder={t('common.search', 'Search users...')}
                />
              </MatrixList.SearchContainer>

              <MatrixList.NameContainer $height={gridHeight}>
                <MatrixList.NameWindow
                  style={{
                    transform: `translate3d(0, ${
                      startRow * MATRIX_CELL_SIZE
                    }px, 0)`,
                  }}
                >
                  {visibleUsers.map(({ user, index }) =>
                    user?.profile ? (
                      <MatrixList.NameContainer.Item
                        key={user.id}
                        $isStriped={index % 2 === 0}
                      >
                        <MatrixList.NameContainer.Item.Name>
                          {getProfileName(user.profile)}
                        </MatrixList.NameContainer.Item.Name>
                      </MatrixList.NameContainer.Item>
                    ) : (
                      <MatrixList.NameContainer.Item
                        key={user?.id || index}
                        $isStriped={index % 2 === 0}
                      />
                    ),
                  )}
                </MatrixList.NameWindow>
              </MatrixList.NameContainer>
            </MatrixList.Left>

            <MatrixList.Right $width={gridWidth}>
              <MatrixList.Right.Title $width={gridWidth}>
                {dataCommunities.map((item) =>
                  item ? (
                    <MatrixList.Right.Title.Container key={item.id}>
                      <MatrixList.Right.Title.Name>
                        <MatrixList.Right.Title.NameBox>
                          {item.name || 'Unnamed Community'}
                        </MatrixList.Right.Title.NameBox>
                      </MatrixList.Right.Title.Name>
                    </MatrixList.Right.Title.Container>
                  ) : null,
                )}
              </MatrixList.Right.Title>

              <MatrixList.Right.Rows $height={gridHeight} $width={gridWidth}>
                <MatrixList.Right.Window
                  style={{
                    transform: `translate3d(${rowLeft}px, ${
                      startRow * MATRIX_CELL_SIZE
                    }px, 0)`,
                  }}
                >
                  {visibleUsers.map(({ user, index }) =>
                    user ? (
                      <MatrixList.Right.Row
                        key={user.id}
                        $isStriped={index % 2 === 0}
                      >
                        {visibleCommunities.map((item) =>
                          item ? (
                            <MatrixList.Right.Cell key={item.id}>
                              <CheckBox
                                isSelected={item.userIds?.includes(user.id)}
                                onChange={(e) =>
                                  onChangeCheckBox(e, user.id, item)
                                }
                                disabled={isCheckboxDisabled(user, item)}
                              />
                            </MatrixList.Right.Cell>
                          ) : null,
                        )}
                      </MatrixList.Right.Row>
                    ) : null,
                  )}
                </MatrixList.Right.Window>
              </MatrixList.Right.Rows>
            </MatrixList.Right>
          </>
        )}
      </MatrixList>
    </>
  );
};

CommunitiesList.propTypes = {
  authUser: shape({
    id: string.isRequired,
  }).isRequired,
  communities: arrayOf(
    shape({
      id: string.isRequired,
      name: string,
      ownerId: string,
      users: arrayOf(
        shape({
          id: string.isRequired,
          profile: shape({
            firstName: string,
            lastName: string,
          }),
        }),
      ),
    }),
  ).isRequired,
  deleteCommunityUser: func.isRequired,
  addCommunityUsers: func.isRequired,
  loadingMatrix: bool,
};

CommunitiesList.defaultProps = {
  loadingMatrix: false,
};

export default CommunitiesList;
